import React, {useCallback, useContext} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {HotelCardContext} from 'components/HotelCardContext'
import {UnavailabilityMobile} from 'components/Unavailability/Mobile'
import {UnavailabilityWithChangeDates} from 'components/Unavailability/UnavailabilityComponent/UnavailabilityWithChangeDates'
import {UnavailabilityWithPopupTrigger} from 'components/Unavailability/UnavailabilityComponent/UnavailabilityWithPopupTrigger'
import useShowOverlay, {
  useShowDatePickerOverlay
} from 'hooks/useShowOverlay/useShowOverlay'
import {hideOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'
import {
  getHotelAvailabilityPrices,
  getHotelAvailabilityPricesComplete
} from 'modules/sapiSearch/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'

interface Props {
  sourceComponent: string
}
const UnavailabilityWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s400};

  ${linariaMq.desktopXs} {
    padding: 0;
    align-self: flex-end;
    &.--is-top-aligned {
      align-self: flex-start;
    }
    &.--is-top-aligned.--is-ua-improvements-a-side {
      margin-top: ${cssTheme.layout.spacing.s300};
    }
  }
`

const UnavailabilityMobileWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s400} 0;
`

const HotelCardUnavailabilityComponent = ({sourceComponent}: Props) => {
  const dispatch = useDispatch()
  const showDatePickerOverlay = useShowDatePickerOverlay()
  const showOverlay = useShowOverlay()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const {hotelId, isAnchor} = useContext(HotelCardContext)

  const hotelAvailabilityPrices = useSelector(state =>
    getHotelAvailabilityPrices(state, hotelId)
  )
  const availabilitySearchComplete = useSelector(
    getHotelAvailabilityPricesComplete
  )

  const hasHotelAvailabilityPrices = Boolean(hotelAvailabilityPrices)
  const isAvailabilityDataLoading =
    !hasHotelAvailabilityPrices && !availabilitySearchComplete

  const handleChangeDatesClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.SoldOutChangeDatesButton,
          action: Action.Clicked
        })
      )
      dispatch(hideOverlay())
      showDatePickerOverlay(DatePickerType.CheckIn)
    },
    [dispatchTeamEvent, dispatch, showDatePickerOverlay]
  )

  const handleShowAvailabilityPopup = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.AvailabilityPaneSeeMore,
          action: Action.Clicked,
          component: sourceComponent
        })
      )
      showOverlay({
        type: OverlayType.AvailabilityPopup
      })
    },
    [dispatchTeamEvent, sourceComponent, showOverlay]
  )

  if (isMobile || isDesktopXs) {
    return (
      <UnavailabilityMobileWrapper>
        <UnavailabilityMobile
          hotelId={hotelId}
          sourceComponentPrefix="MobileHotelView"
        />
      </UnavailabilityMobileWrapper>
    )
  }

  return (
    <UnavailabilityWrapper
      className={cx(
        isAnchor && '--is-top-aligned',
        '--is-ua-improvements-a-side'
      )}
      data-id="SoldOutPane"
    >
      {isAnchor ? (
        <UnavailabilityWithPopupTrigger
          handleShowAvailabilityPopup={handleShowAvailabilityPopup}
          handleChangeDatesClick={handleChangeDatesClick}
          showViewPopupCalendar={hasHotelAvailabilityPrices}
          isLoading={isAvailabilityDataLoading}
        />
      ) : (
        <UnavailabilityWithChangeDates
          hasCompactButton
          handleChangeDatesClick={handleChangeDatesClick}
        />
      )}
    </UnavailabilityWrapper>
  )
}

export const HotelCardUnavailability = React.memo(
  HotelCardUnavailabilityComponent
)
